import React, { useCallback, useEffect, useMemo } from 'react';
import T from 'i18n';
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { Close, DragIndicator, InfoOutlined, FolderCopyOutlined } from '@mui/icons-material';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

type SourceFile = { id: string; name: string; citationsCount?: number };

const deriveSourceFilesFromCitations = (
  citationsBySource: any,
  fileIdToName: Record<string, string>,
) => {
  const filesById = new Map<string, SourceFile>();
  const counts: Record<string, number> = {};

  if (!citationsBySource || typeof citationsBySource !== 'object') {
    return { files: [], counts };
  }

  Object.keys(citationsBySource).forEach((key) => {
    const citationObj = citationsBySource[key];
    if (!citationObj) return;
    const metadata = citationObj?.metadata || {};
    const rawId = metadata?.fileId ?? metadata?.id ?? citationObj?.fileId;
    if (!rawId) return;

    const id = String(rawId);
    const name =
      metadata?.fileName ||
      metadata?.filename ||
      metadata?.name ||
      citationObj?.documentName ||
      fileIdToName?.[id] ||
      id;

    if (!name) return;
    counts[id] = (counts[id] || 0) + 1;

    if (!filesById.has(id)) {
      filesById.set(id, { id, name, citationsCount: counts[id] });
    } else {
      filesById.set(id, { ...(filesById.get(id) as SourceFile), citationsCount: counts[id] });
    }
  });

  return { files: Array.from(filesById.values()), counts };
};

const DraggableSourceFileItem = React.memo(
  ({
    file,
    allFiles,
    selectedIdsRef,
    isSelected,
    onItemClick,
    onCmdEnterToggle,
    citationCount,
  }: {
    file: { id: string; name: string };
    allFiles: Array<{ id: string; name: string }>;
    selectedIdsRef: React.MutableRefObject<string[]>;
    isSelected: boolean;
    onItemClick: (e: React.MouseEvent) => void;
    onCmdEnterToggle: () => void;
    citationCount: number;
  }) => {
    const [, drag, preview] = useDrag(() => ({
      type: 'DOCUMENT',
      canDrag: () => {
        const selectedIds = selectedIdsRef.current || [];
        return selectedIds.length > 0 && selectedIds.includes(file.id);
      },
      item: () => {
        const selectedIds = selectedIdsRef.current || [];
        const useIds = selectedIds;
        const docs = allFiles
          .filter((f) => useIds.includes(f.id))
          .map((f) => ({ id: f.id, name: f.name }));
        return {
          documentIds: useIds,
          dragRow: { id: file.id, name: file.name },
          type: 'DOCUMENT',
          isAttachment: true,
          attachmentDocuments: docs,
        };
      },
      options: { dropEffect: 'copy' },
    }));

    useEffect(() => {
      preview(getEmptyImage(), { captureDraggingState: true });
    }, [preview]);

    return (
      <ListItem
        ref={drag}
        onClick={onItemClick}
        onKeyDown={(e: React.KeyboardEvent) => {
          if ((e.metaKey || e.ctrlKey) && (e.key === 'Enter' || e.keyCode === 13)) {
            e.preventDefault();
            onCmdEnterToggle();
          }
        }}
        tabIndex={0}
        role="button"
        sx={{
          py: 0.5,
          cursor: 'pointer',
          ...(isSelected && { backgroundColor: 'action.selected' }),
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {isSelected && <DragIndicator color="primary" sx={{ mr: 1, flexShrink: 0 }} />}
        <ListItemText primary={file.name} secondary={`ID: ${file.id}`} sx={{ flex: 1, mr: 2 }} />
        <Box
          sx={{
            alignSelf: 'center',
            ml: 1,
            px: 1,
            py: '2px',
            bgcolor: '#F7F7F8',
            border: '1px solid #85A0F1',
            borderRadius: '16px',
            fontSize: 12,
            lineHeight: 1,
            minWidth: 24,
            textAlign: 'center',
            flexShrink: 0,
          }}
        >
          {citationCount || 0}
        </Box>
      </ListItem>
    );
  },
);

type SourceFilesMenuProps = {
  citationsBySource: any;
  fileIdToName: Record<string, string>;
  buttonClassName?: string;
};

export default function SourceFilesMenu({
  citationsBySource,
  fileIdToName,
  buttonClassName,
}: SourceFilesMenuProps) {
  const [sourceMenuAnchorEl, setSourceMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [sourceMenuFiles, setSourceMenuFiles] = React.useState<SourceFile[]>([]);
  const [sourceMenuPlacement, setSourceMenuPlacement] = React.useState<
    'bottom-start' | 'top-start'
  >('bottom-start');
  const [sourceMenuSelectedIds, setSourceMenuSelectedIds] = React.useState<string[]>([]);
  const sourceMenuSelectedIdsRef = React.useRef<string[]>([]);
  const [sourceMenuCitationsCount, setSourceMenuCitationsCount] = React.useState<
    Record<string, number>
  >({});

  const derivedSourceFiles = useMemo(
    () => deriveSourceFilesFromCitations(citationsBySource, fileIdToName),
    [citationsBySource, fileIdToName],
  );

  const totalSourceMenuCitations = useMemo(() => {
    if (!Array.isArray(sourceMenuFiles) || sourceMenuFiles.length === 0) return 0;
    return sourceMenuFiles.reduce((sum, sourceFile) => {
      return sum + (typeof sourceFile.citationsCount === 'number' ? sourceFile.citationsCount : 0);
    }, 0);
  }, [sourceMenuFiles]);

  useEffect(() => {
    sourceMenuSelectedIdsRef.current = sourceMenuSelectedIds;
  }, [sourceMenuSelectedIds]);

  const handleToggleSourceMenu = useCallback(
    (anchorEl: HTMLElement) => {
      const { files, counts } = derivedSourceFiles;
      if (files.length === 0) return;
      setSourceMenuCitationsCount(counts);
      setSourceMenuFiles(files);
      try {
        const rect = anchorEl.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const preferredHeight = Math.min(window.innerHeight * 0.6, 400);
        setSourceMenuPlacement(spaceBelow >= preferredHeight ? 'bottom-start' : 'top-start');
      } catch {
        setSourceMenuPlacement('bottom-start');
      }
      setSourceMenuAnchorEl((prev) => (prev === anchorEl ? null : anchorEl));
      setSourceMenuSelectedIds([]);
    },
    [derivedSourceFiles],
  );

  const handleCloseSourceMenu = useCallback(() => {
    setSourceMenuAnchorEl(null);
    setSourceMenuSelectedIds([]);
  }, []);

  useEffect(() => {
    if (!sourceMenuAnchorEl) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseSourceMenu();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [sourceMenuAnchorEl, handleCloseSourceMenu]);

  if (!derivedSourceFiles.files.length) return null;

  return (
    <>
      <Tooltip title={T.translate('generic.viewSourceFiles')} placement="top" arrow>
        <button
          type="button"
          className={buttonClassName}
          aria-label={T.translate('generic.sourceFiles')}
          onClick={(e) => handleToggleSourceMenu(e.currentTarget as HTMLElement)}
        >
          <FolderCopyOutlined fontSize="small" />
          <span style={{ marginLeft: '6px' }}>{T.translate('generic.sourceFiles')}</span>
        </button>
      </Tooltip>
      <Popper
        open={Boolean(sourceMenuAnchorEl)}
        anchorEl={sourceMenuAnchorEl}
        placement={sourceMenuPlacement}
        modifiers={[
          { name: 'flip', options: { fallbackPlacements: ['top-start', 'bottom-start'] } },
          { name: 'preventOverflow', options: { boundary: 'viewport' } },
          { name: 'offset', options: { offset: [0, 8] } },
        ]}
        sx={{ zIndex: 99 }}
      >
        <ClickAwayListener
          onClickAway={(event) => {
            const target = (event as unknown as MouseEvent | TouchEvent).target as Node;
            if (sourceMenuAnchorEl && target && sourceMenuAnchorEl.contains(target)) return;
            handleCloseSourceMenu();
          }}
        >
          <Paper
            elevation={0}
            sx={{
              minWidth: 250,
              maxWidth: 450,
              maxHeight: '50vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              border: '1px solid #e5e7eb',
              borderRadius: '4px',
              boxShadow: '0 16px 44px rgba(15, 23, 42, 0.14)',
            }}
          >
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, fontSize: '12px' }}>
                {T.translate('generic.sourceFiles')} ({sourceMenuFiles.length})
                <Box component="span" sx={{ ml: 2 }}>
                  {T.translate('generic.citations')} ({totalSourceMenuCitations})
                </Box>
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  size="small"
                  aria-label={T.translate('generic.close')}
                  onClick={handleCloseSourceMenu}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Divider />
            {sourceMenuFiles && sourceMenuFiles.length > 0 && (
              <>
                <Box sx={{ px: 2, py: 1, pt: 1, pb: 0, display: 'flex', alignItems: 'center' }}>
                  <InfoOutlined fontSize="small" sx={{ color: '#85A0F1', mr: 0.5 }} />
                  <Typography variant="body2" color="text.secondary" fontSize="11px">
                    {T.translate('generic.dragAndDropSourceFilesInfo')}
                  </Typography>
                </Box>
                <Box sx={{ px: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button
                    size="small"
                    onClick={() => setSourceMenuSelectedIds(sourceMenuFiles.map((f) => f.id))}
                    disabled={sourceMenuSelectedIds.length === sourceMenuFiles.length}
                    sx={{
                      textTransform: 'none',
                      pl: 0,
                      border: '1px solid #85A0F1',
                      padding: '1px 4px',
                      margin: '5px 0',
                      '&.Mui-disabled': { borderColor: '#cdcdcd' },
                    }}
                  >
                    {T.translate('generic.selectAllFiles')}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setSourceMenuSelectedIds([])}
                    disabled={sourceMenuSelectedIds.length === 0}
                    sx={{
                      textTransform: 'none',
                      pl: 0,
                      border: '1px solid #85A0F1',
                      padding: '1px 4px',
                      margin: '5px 0',
                      '&.Mui-disabled': { borderColor: '#cdcdcd' },
                    }}
                  >
                    {T.translate('generic.clearAllFiles')}
                  </Button>
                </Box>
              </>
            )}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                paddingRight: '4px',
                scrollbarGutter: 'stable',
                scrollbarWidth: 'thin',
                scrollbarColor: 'transparent transparent',
                '&::-webkit-scrollbar': {
                  width: '6px',
                  height: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                },
                '&:hover': {
                  scrollbarColor: 'rgba(0, 0, 0, 0.35) transparent',
                },
                '&:hover::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0, 0, 0, 0.35)',
                },
              }}
            >
              {sourceMenuFiles && sourceMenuFiles.length > 0 ? (
                <List dense>
                  {sourceMenuFiles.map((f) => {
                    const isSelected = sourceMenuSelectedIds.includes(f.id);
                    return (
                      <DraggableSourceFileItem
                        key={f.id}
                        file={f}
                        allFiles={sourceMenuFiles}
                        selectedIdsRef={sourceMenuSelectedIdsRef}
                        isSelected={isSelected}
                        citationCount={
                          typeof f.citationsCount === 'number'
                            ? f.citationsCount
                            : sourceMenuCitationsCount[f.id] || 0
                        }
                        onItemClick={() => {
                          setSourceMenuSelectedIds((prev) =>
                            isSelected ? prev.filter((id) => id !== f.id) : [...prev, f.id],
                          );
                        }}
                        onCmdEnterToggle={() => {
                          setSourceMenuSelectedIds((prev) =>
                            prev.includes(f.id)
                              ? prev.filter((id) => id !== f.id)
                              : [...prev, f.id],
                          );
                        }}
                      />
                    );
                  })}
                </List>
              ) : (
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="body2">No source files.</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
